import { useCallback } from "react";
import { getVersionLabels, type LabelQuery } from "../../../api/content/versionsApi";
import type { VersionLabel } from "../../../api/content/versionDecoders";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";

/**
 * A label names exactly one version, forever (ADR-0033), so a label once read is kept for
 * the session. A version without a label is asked again: it may be labelled since.
 */
const knownLabels = new Map<string, VersionLabel>();
const cacheKey = (projectId: string, reference: string) => `${projectId} ${reference}`;

/** The label of each reference, `null` where it has none; read outside React too (a Workflow's catalogue). */
export async function resolveVersionLabels(projectId: string, query: LabelQuery, signal: AbortSignal): Promise<ReadonlyMap<string, VersionLabel | null>> {
  const labels = new Map<string, VersionLabel | null>();
  const unknown = (references: readonly string[]) =>
    [...new Set(references)].filter((reference) => {
      const known = knownLabels.get(cacheKey(projectId, reference));
      if (known !== undefined) labels.set(reference, known);
      return known === undefined;
    });
  // One request: the server owns how many references it takes; the views ask for one page at most.
  const itemVersions = unknown(query.itemVersions);
  const collectionVersions = unknown(query.collectionVersions);
  if (itemVersions.length === 0 && collectionVersions.length === 0) return labels;
  const answers = await getVersionLabels(projectId, { itemVersions, collectionVersions }, signal);
  for (const { reference, label } of answers) {
    labels.set(reference, label);
    if (label !== null) knownLabels.set(cacheKey(projectId, reference), label);
  }
  return labels;
}

const NOTHING_TO_LABEL: ReadonlyMap<string, VersionLabel | null> = new Map();

/**
 * The labels of exact versions, for showing them by name. A failed or unfinished lookup
 * leaves the caller to show names without versions; the exact references stay in Details.
 */
export function useVersionLabels(projectId: string, query: LabelQuery): LoadState<ReadonlyMap<string, VersionLabel | null>> {
  // The lists by value, so a new array with the same references doesn't load again.
  const itemKey = query.itemVersions.join("\n");
  const collectionKey = query.collectionVersions.join("\n");
  const load = useCallback(
    (signal: AbortSignal) => {
      const itemVersions = itemKey === "" ? [] : itemKey.split("\n");
      const collectionVersions = collectionKey === "" ? [] : collectionKey.split("\n");
      if (itemVersions.length === 0 && collectionVersions.length === 0) return Promise.resolve(NOTHING_TO_LABEL);
      return resolveVersionLabels(projectId, { itemVersions, collectionVersions }, signal);
    },
    [projectId, itemKey, collectionKey],
  );
  return useLoad(load).state;
}

/** The reference's label once the lookup has it; `null` before, on failure, or without one. */
export function labelOf(labels: LoadState<ReadonlyMap<string, VersionLabel | null>>, reference: string | null): VersionLabel | null {
  if (reference === null || labels.status !== "loaded") return null;
  return labels.value.get(reference) ?? null;
}
